
import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { fetchLocationStats } from "../api"; // must support ?start & ?end query

export default function LocationGraph() {
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("pie");
  const [range, setRange] = useState({ start: "", end: "" });

const COLORS = [
  "#FF6B6B", // red
  "#4ECDC4", // teal
  "#556270", // dark gray
  "#C7F464", // lime
  "#FFCC5C", // yellow
  "#C44DFF", // purple
];


  // Fetch data
  const loadLocationStats = async (start, end) => {
    setLoading(true);
    try {
      const res = await fetchLocationStats(start, end);
      const rawData = res?.data?.data || [];

      const processed = rawData
        .map((d) => ({
          ...d,
          _id: d._id || "Unknown",
        }))
        .filter((d) => d.totalHours > 0 || d.count > 0);

      setLocationData(processed);
    } catch (err) {
      console.error("Error fetching location stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Default load → current month
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split("T")[0];
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
    setRange({ start, end });
    loadLocationStats(start, end);
  }, []);

  // CSV Download
  const handleDownloadCSV = () => {
    if (!locationData.length) return alert("No data to download!");

    const csvRows = [];
    csvRows.push(["Location", "Total Hours", "Staff Count"]);

    locationData.forEach((d) => {
      csvRows.push([
        `"${d._id}"`,
        d.totalHours.toFixed(2),
        d.count || 0,
      ]);
    });

    const csvString = csvRows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const filename = `Location_Working_Hours_${range.start}_to_${range.end}.csv`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 🗓 Handle date change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    if (!range.start || !range.end) {
      alert("Please select both start and end dates!");
      return;
    }
    loadLocationStats(range.start, range.end);
  };

  const legendPayload = locationData.map((d, i) => ({
    value: d._id,
    id: d._id,
    type: "square",
    color: COLORS[i % COLORS.length],
  }));

  const CHART_HEIGHT = 160;

  return (
    <div className="p-4 bg-white rounded-xl shadow-sm w-full max-w-2xl mx-auto">
<div className="flex flex-col mb-4 gap-3">
  {/* Title */}
  <h2 className="text-lg font-semibold text-gray-800">Client Hours</h2>

  {/* 🗓 Date Pickers */}
  <div className="flex items-center gap-2 flex-wrap">
    <input
      type="date"
      name="start"
      value={range.start}
      onChange={handleDateChange}
      className="border rounded-md px-2 py-1 text-sm w-[130px]"
    />
    <span className="text-gray-500 text-sm">to</span>
    <input
      type="date"
      name="end"
      value={range.end}
      onChange={handleDateChange}
      className="border rounded-md px-2 py-1 text-sm w-[130px]"
    />
    <button
      onClick={handleApply}
      className="bg-indigo-600 text-white rounded-md text-sm px-3 py-1 hover:bg-indigo-700"
    >
      Apply
    </button>
  </div>
</div>


      {/*  Toggle + Download */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => setActiveView("pie")}
            className={`px-3 py-1 rounded-full ${
              activeView === "pie"
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Pie
          </button>
          <button
            onClick={() => setActiveView("bar")}
            className={`px-3 py-1 rounded-full ${
              activeView === "bar"
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Bar
          </button>
        </div>

        <button
          onClick={handleDownloadCSV}
          className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
        >
          Report
        </button>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-gray-500">Loading Location Stats...</p>
        </div>
      ) : locationData.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-6">
          No data available for selected range.
        </p>
      ) : (
        <>
          {/* === PIE VIEW === */}
        {activeView === "pie" && (
  <div className="p-3 rounded-lg border border-gray-100 bg-gray-50">
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <PieChart>
        <Tooltip
          formatter={(value, name, props) => [
            `${value.toFixed(2)} Hrs`,
            props.payload._id,
          ]}
          contentStyle={{ border: "1px solid #ccc", fontSize: "12px" }}
        />

        <Pie
          data={locationData}
          dataKey="totalHours"
          nameKey="_id"
          cx="50%"
          cy="50%"
          outerRadius={80}
          innerRadius={45}    // ← donut hole (needed for center text)
          paddingAngle={0}
          labelLine={false}
        >
          {locationData.map((entry, i) => (
            <Cell
              key={`pie-${i}`}
              fill={COLORS[i % COLORS.length]}
              stroke={COLORS[i % COLORS.length]}
            />
          ))}
        </Pie>

        {/* === CENTER TEXT === */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: "14px", fontWeight: "600", fill: "#374151" }}
        >
          {locationData.reduce((sum, d) => sum + d.totalHours, 0).toFixed(1)} Hrs
        </text>
      </PieChart>
    </ResponsiveContainer>
  </div>
)}


          {/* === BAR VIEW === */}
          {activeView === "bar" && (
            <div className="p-3 rounded-lg border border-gray-100 bg-gray-50">
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart
                  data={locationData}
                  margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="_id"
                    tickLine={false}
                    axisLine={false}
                    style={{ fontSize: "10px" }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    style={{ fontSize: "10px" }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                    contentStyle={{ border: "1px solid #ccc", fontSize: "12px" }}
                    formatter={(v) => [`${v.toFixed(2)} Hrs`, "Total Hours"]}
                  />
                  <Legend
                    payload={legendPayload}
                    iconSize={8}
                    wrapperStyle={{ fontSize: "11px" }}
                  />
                  <Bar dataKey="totalHours" radius={[4, 4, 0, 0]} barSize={18}>
                    {locationData.map((entry, i) => (
                      <Cell key={`bar-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
